import { useCallback, useRef, useState } from "react";

/**
 * Das Toast-Gegenstueck zu `toast(msg)` (`index.html:5264-5270`). Das `#toast`-CSS ist bereits
 * portiert (`bestand.css:2474-2480`, dunkel `:2629`) - diese Komponente liefert ausschliesslich das
 * dazugehoerige Markup. Das Setzen/Raeumen des Texts uebernimmt `useMeldungsAnzeige` unten (Original:
 * `t.textContent = msg` + `t.classList.add('show')` + `setTimeout(..., 2200)`, index.html:5266-5269)
 * - diese Komponente selbst haelt keinen eigenen Zustand und keinen eigenen Timer.
 *
 * NUR FUER BESTAETIGUNGEN UND NEUTRALE HINWEISE: Der Toast zeigt eine Meldung 2,2 s lang in einem
 * einzigen Slot - fuer einen Fehler ist das zu fluechtig und eine zweite Meldung verdraengt die
 * erste. Alles, was sagt, dass die Absicht des Nutzers nicht oder nur teilweise ausgefuehrt wurde,
 * geht an den Fehlerstapel (`state/fehlerStapel.ts#meldeFehler`, Anzeige `FehlerStapel.tsx`), der
 * stehen bleibt, bis man ihn schliesst. Das ist eine BEWUSSTE ABWEICHUNG VOM BESTAND (Spec
 * 2026-10-19-fehleranzeige-fundament-design.md, Abschnitt 1): `index.html` kennt nur den Toast, ist
 * aber historisch und wird nicht weitergepflegt (AGENTS.md). Die Woertlichkeitsregel des Ports gilt
 * fuer den Toast weiter, fuer Fehler nicht mehr.
 *
 * GEFUNDENE ABWEICHUNG ZUM BRIEF (Quelltext gilt, Woertlichkeitsregel): der Brief nennt "2600 ms"
 * und verweist auf `index.html:5266` - dort steht aber `t.textContent = msg`, nicht der Timeout.
 * Der tatsaechliche `setTimeout`-Aufruf steht an `index.html:5269` und traegt `2200`, nicht 2600.
 * Uebernommen wurde der Quelltextwert: `TOAST_ANZEIGE_DAUER_MS = 2200`.
 */
export const TOAST_ANZEIGE_DAUER_MS = 2200;

export interface ToastProps {
  /** `null` ist der Ruhezustand (keine Meldung, keine Klasse `show`). */
  text: string | null;
}

export function Toast({ text }: ToastProps) {
  return (
    <div id="toast" className={text ? "show" : ""}>
      {text}
    </div>
  );
}

/**
 * Der Zeitvertrag hinter `toast(msg)` als eigenstaendige, exportierte Einheit - Fix-Runde 1
 * (Review-Critical): vorher stand dieselbe Logik als Kopie sowohl in `App.tsx#zeigeMeldung` als
 * auch (fuer den Test) in `MeldungsTraeger` in `toast.test.tsx`. Eine Kopie kann vom Original
 * abdriften, ohne dass ein Test es merkt (Wirksamkeitsnachweis am ECHTEN Pruefling war nicht
 * moeglich). Jetzt gibt es nur noch DIESE Implementierung: `App.tsx` bezieht `zeigeMeldung`
 * ausschliesslich hierueber, `toast.test.tsx` importiert und rendert denselben Haken.
 *
 * `clearTimeout` vor dem neuen `setTimeout` ist 1:1 das Original-`clearTimeout(t._t)`
 * (index.html:5268) - ohne diese Zeile wuerde eine Meldung, die kurz nach einer vorherigen kommt,
 * von deren bereits laufendem Timer vorzeitig abgeraeumt (siehe Testfall "eine neue Meldung setzt
 * den Ausblendtimer der vorherigen zurueck").
 *
 * `dauerMs` ist ausschliesslich fuer Tests parametrisierbar (Default = `TOAST_ANZEIGE_DAUER_MS`,
 * derselbe Wert, den auch `App.tsx` verwendet); productive Aufrufer lassen den Parameter weg.
 *
 * `useCallback` fuer `zeigeMeldung` (Folgefix zu Auftrag "Bestellboard-Filter — Renderausfall durch
 * fehlende Memoisierung beheben", ergaenzend zu den drei dort benannten `App.tsx`-Handlern
 * entdeckt): `App.tsx` reicht das Ergebnis dieses Hooks unveraendert als `onMeldung` an
 * `<BoardSeite>` durch, und `BoardSeite.tsx#zelleAngeklickt` fuehrt `onMeldung` in SEINEM eigenen
 * Abhaengigkeitsarray (-> `onBearbeiten` an jede `Bestellzeile`). Ohne diesen `useCallback` waere
 * `zeigeMeldung` bei JEDEM Aufruf dieses Hooks (also bei JEDEM Render der aufrufenden Komponente)
 * eine neue Funktion - selbst ein `App.tsx`, dessen drei benannte Handler (`drawerOeffnen`/
 * `kontextmenueOeffnen`/`boardZelleAendern`) sauber referenzstabil sind, risse ueber GENAU DIESE
 * Kette bei jedem eigenen Render trotzdem wieder JEDE Bestellzeile mit - gemessen in
 * `test/komponenten/App/appHandlerRerender.test.tsx`: ohne diese Zeile blieb der dortige Test auch
 * NACH dem `App.tsx`-Fix rot (85 statt 0 Aufrufe). `dauerMs` als Abhaengigkeit, weil gelesen -
 * productive Aufrufer geben es nie mit, es aendert sich also praktisch nie.
 */
export function useMeldungsAnzeige(
  dauerMs: number = TOAST_ANZEIGE_DAUER_MS,
): readonly [string | null, (text: string) => void] {
  const [meldung, setMeldung] = useState<string | null>(null);
  const timer = useRef<number | undefined>(undefined);

  const zeigeMeldung = useCallback(
    (text: string): void => {
      setMeldung(text);
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setMeldung(null), dauerMs);
    },
    [dauerMs],
  );

  return [meldung, zeigeMeldung] as const;
}
